import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID } from '../config.js';
import { removeCookie } from '../utils/cookies.js';
import { endAllSessions, startSessionWatch, isSessionLoss } from '@globegroupe-data/auth-kit/browser';

// Create the context
const AuthContext = createContext();

// Marqueur du rebond automatique vers Connect. En sessionStorage et non en mémoire :
// la redirection quitte la page, donc un ref repartirait à zéro au retour et on
// bouclerait indéfiniment si Connect ne rétablit aucune session.
// Le sessionStorage est partagé par toutes les frames same-origin de l'onglet : le
// marqueur dédoublonne donc aussi les logins concurrents shell + iframes /c/:slug.
// C'est vital : /auth/login régénère la session, deux flux simultanés se détruisent
// mutuellement (state PKCE perdu, tokens effacés) et tout finit en 401.
const BOUNCE_KEY = 'cockpit.connectBounce';

// Dans une iframe, un login interactif afficherait l'écran de login Connect DANS le
// cadre. Les rebonds automatiques y passent en silent=1 (prompt=none, cf. auth-kit) :
// Connect répond sans UI, et l'échec revient en ?connect_error=<code> sur le returnTo.
const IN_IFRAME = window.self !== window.top;

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');

  // Redirection PLEINE PAGE vers le backend, qui enchaîne sur /oauth/authorize de Connect.
  // Sans option : interactif (le clic « Utiliser mon compte Globe » doit pouvoir
  // afficher l'écran Connect, même dans une iframe — silent y répondrait login_required).
  const loginWithConnect = useCallback(({ silent = false } = {}) => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}${silent ? '&silent=1' : ''}`);
  }, []);

  // Rebond automatique, personne n'a rien cliqué : une seule fois par onglet.
  // Rend false quand le rebond a déjà été tenté, à l'appelant de proposer autre chose.
  const loginWithConnectOnce = useCallback(() => {
    if (sessionStorage.getItem(BOUNCE_KEY)) return false;
    sessionStorage.setItem(BOUNCE_KEY, '1');
    loginWithConnect({ silent: IN_IFRAME });
    return true;
  }, [loginWithConnect]);

  // useCallback : des effets de chargement (Cockpit.js) dépendent de cette identité ;
  // recréée à chaque rendu du provider, elle remontait leurs iframes à chaque état
  // qui bouge ici, et alimentait la boucle de re-fetch sur /cockpits/:slug.
  //
  // L'auth voyage dans le cookie de session (BFF), jamais dans un header : un header
  // Authorization fabriqué côté front court-circuitait le challenge `Session` du
  // backend et sabotait la reconnexion silencieuse (cf. middleware/auth.js).
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const response = await fetch(url, { ...options, credentials: 'include' });
    // isSessionLoss et pas le statut : seuls les 401 qui portent le challenge
    // `WWW-Authenticate: Session` viennent de la couche session (logout ailleurs,
    // refresh refusé). Un 401 applicatif — jeton de service refusé, API tierce —
    // ne doit déconnecter personne.
    // Gated on isAuthenticated, not on the path: an anonymous visitor on a public
    // /c/:slug must not bounce, but someone who WAS signed in must, and that is
    // exactly where the iframes live — excluding the path left the shell logged in
    // while its own iframes fell.
    if (isSessionLoss(response) && !options.noRedirect && isAuthenticated) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      loginWithConnectOnce();
    }
    return response;
  }, [isAuthenticated, loginWithConnectOnce]);

  const logout = async () => {
    // Session BFF + session SSO : Connect révoque tous les jetons qui en
    // descendent, donc les autres apps tombent aussi.
    await endAllSessions({
      apiBaseUrl: API_BASE_URL,
      connectUrl: CONNECT_UI_URL,
      clientId: CONNECT_CLIENT_ID,
    });
    loginWithConnect();
  };

  // The 401 handler only fires when the app makes a call; this covers returning to
  // a tab whose session died elsewhere (focus-only: no more polling — user action
  // + tab focus are enough, see auth-kit 0.10.0).
  // Cockpit is THE logout judge: it alone redirects to the login; embedded apps
  // only ever do silent recovery.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return startSessionWatch({
      probeUrl: `${API_BASE_URL}/api/me`,
      onSessionLost: () => {
        setIsAuthenticated(false);
        setCurrentUser(null);
        loginWithConnectOnce();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Session rétablie : le marqueur de rebond peut être relâché.
  useEffect(() => {
    if (isAuthenticated) sessionStorage.removeItem(BOUNCE_KEY);
  }, [isAuthenticated]);

  // Check authentication status and fetch user data on mount
  useEffect(() => {
    // Sonde Connect : une seule requête, /api/me décide de tout.
    const checkConnectAuth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/me`, { credentials: 'include' });

        if (response.ok) {
          setCurrentUser(await response.json());
          setIsAuthenticated(true);
        } else if (response.status === 403) {
          // Session Connect valide mais aucun utilisateur Cockpit correspondant.
          // Surtout PAS de redirection ici, sinon boucle de login infinie.
          setLoginError("Utilisateur inconnu dans Cockpit. Contactez un administrateur.");
        } else if (isSessionLoss(response)) {
          // Pas de session (le backend le dit via le challenge `Session`) : on part
          // chercher un token chez Connect. Sauf sur /c/:slug, la route d'embed qui
          // sert les cockpits publics à des visiteurs anonymes et ne doit jamais
          // rebondir sur un écran de login. Un 401 sans challenge tombe dans le
          // `else` : erreur affichée, aucune redirection.
          if (!window.location.pathname.startsWith('/c/')) {
            // Once et pas loginWithConnect : ce chemin part à CHAQUE montage. Sans le
            // marqueur, shell et iframes déclenchent chacun leur /auth/login et les
            // régénérations de session se 401-isent mutuellement (« double login »).
            if (loginWithConnectOnce()) return;
            setLoginError('Session expirée. Rechargez la page pour vous reconnecter.');
          }
        } else {
          // 5xx, 429, etc. : surtout pas de redirection. Connect renverrait un token
          // valide, /api/me échouerait à nouveau, et on boucherait tout le temps de
          // l'incident. On affiche l'erreur et on s'arrête là.
          setLoginError(`Service indisponible (erreur ${response.status}).`);
        }
      } catch (error) {
        console.error('Connect auth check failed:', error);
        setLoginError('Connexion impossible');
      }

      setIsAuthenticating(false);
    };

    // Retour d'un login silencieux échoué : /auth/callback renvoie l'échec en
    // ?connect_error=<code> sur le returnTo. Le consommer comme « rebond déjà
    // tenté » fait retomber tous les chemins existants sur leur repli (bouton
    // manuel, message d'erreur) au lieu de relancer un login en boucle.
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect_error')) {
      sessionStorage.setItem(BOUNCE_KEY, '1');
      params.delete('connect_error');
      const qs = params.toString();
      window.history.replaceState(null, '',
        window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }

    // Purge du jeton de l'ancienne auth Basic. Ce n'est pas un jeton opaque mais
    // base64(login:motdepasse) : le mot de passe en clair, décodable. Plus rien ne
    // le lit ici, mais des apps non migrées (Weekly) le posent encore sur tout
    // .globe-groupe.com — laissé en place, il fait basculer les apps sœurs sur leur
    // repli legacy au pire moment (session morte) et bloque leur reconnexion
    // silencieuse. On le supprime à chaque montage tant que ces apps existent.
    localStorage.removeItem('authToken');
    removeCookie('authToken');

    checkConnectAuth();
  }, []);

  // Value to be provided by the context
  const value = {
    isAuthenticating,
    isAuthenticated,
    currentUser,
    loginError,
    loginWithConnect,
    loginWithConnectOnce,
    logout,
    fetchWithAuth
  };

  // Show loading spinner while authenticating
  if (isAuthenticating) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
